import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { getShortId } from "@/lib/string";
import { useVersions } from "@/services/files.service";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import Link from "next/link";
import { DataDisplay, DataDisplayDate } from "../Drawers/DataDisplay";
import { None } from "../Tables/Logs/LogCells";
import Tooltip from "@components/library/Tooltip";
import { getFileFromLog, Log } from "@/types/app/log";
import { EntityIcon } from "@components/library/Entities/EntityIcon";
import { useRouter } from "next/router";
import { FLOW_ID_PREFIX } from "@/lib/constants";
import { capitalize } from "lodash";
import { Tag } from "@components/library/v2/Tag";
import { classNames } from "@/lib/utils";
import { RewindIcon } from "@heroicons/react/solid";
import { getFileHref } from "@/lib/path-utils";

interface Props {
  log: Log;
}

const LogDrawerHeader = ({ log }: Props) => {
  const file = getFileFromLog(log);
  const { versions, mutate: mutateVersions } = useVersions(file.id);
  const router = useRouter();

  // Attempt to find full Version. Otherwise fall back to version ID.
  const version = versions?.find((v) => v.version_id === file.version_id);
  const entityLabelVersionProps = version ? { file: version } : { fileId: file.id, versionId: file.version_id };
  const { slug } = useActiveOrganization();

  // Show the option to navigate to a Trace in two scenarios:
  // 1. The user checks an inidivdual Log from another File that is part of a Trace.
  // 2. The user is inspecting head of a Trace in LogsTable->TraceDrawer that is part of another Trace
  const showLinkToTrace =
    // The user is looking at a Log outside a LogsTable->TraceDrawer context
    (log.trace_id && !(router.query.projectId as string).startsWith(FLOW_ID_PREFIX)) ||
    // The user is looking at a Log inside a TraceDrawer context
    ((router.query.projectId as string).startsWith(FLOW_ID_PREFIX) &&
      // The Log selected inside TraceDrawer is a Flow Log
      getFileFromLog(log).type === "flow" &&
      // That is part of another Trace
      !!log.trace_id &&
      // Edge case: We are looking at a Flow Log in the context
      // of a TraceDrawer that display the parent Trace owning the Flow Log
      // ; then do not show it.
      getFileFromLog(log).id === (router.query.projectId as string));

  return (
    <div className="flex w-full items-center">
      <div className="flex w-full flex-col gap-12">
        {/* Smaller gap when displaying the link to parent Trace on a Flow Log */}
        <div
          className={classNames(
            "flex items-center ",
            showLinkToTrace && getFileFromLog(log).type === "flow" ? "gap-[26px]" : "gap-32",
          )}
        >
          <DataDisplay
            label={capitalize(getFileFromLog(log).type)}
            value={<FileLabelWithPopover {...entityLabelVersionProps} size={20} displayFile target="_self" />}
          />
          <DataDisplayDate label="Created" datetime={log.created_at} />
          {log.log_status !== null && (
            <DataDisplay
              label="Status"
              value={
                <Tag
                  size={20}
                  className="text-12-12 font-medium"
                  color={log.log_status === "complete" ? "green" : "yellow"}
                >
                  {capitalize(log.log_status!)}
                </Tag>
              }
            />
          )}
          {/* Display a navigation Link to Trace that owns the Log outside TraceDrawer context */}
          {showLinkToTrace && (
            <DataDisplay
              label="Trace"
              value={
                <Link href={`${getFileHref({ slug, id: log.trace_flow_id ?? "", page: "logs" })}?id=${log.trace_id}`}>
                  {log.trace_id ? (
                    // TODO: turn this into an EntityLabelWithPopover for session
                    <Tooltip content="View Trace" side="top">
                      <Tag size={20} color="gray">
                        <div className="flex items-center justify-between gap-x-2">
                          {/* TODO: we shall clean this up */}
                          {log.trace_flow_id?.startsWith("ag") ? (
                            <EntityIcon type="agent" size={12} />
                          ) : (
                            <EntityIcon type="flow" size={12} />
                          )}
                          <div className="text-12-12 font-medium">{getShortId(log.trace_id)}</div>
                        </div>
                      </Tag>
                    </Tooltip>
                  ) : (
                    <None />
                  )}
                </Link>
              }
            />
          )}
          {log.session_id && (
            <DataDisplay
              label="Session"
              value={
                <Link
                  href={`${getFileHref({ slug, id: file.id, page: "sessions" })}/${log.session_id}?datumId=${log.id}`}
                >
                  {log.session_id ? (
                    // TODO: turn this into an EntityLabelWithPopover for session
                    <Tooltip content="View Session" side="top">
                      <span>
                        <Tag size={20} shade="gray" className="flex items-center gap-4">
                          <RewindIcon className="h-16 w-16 text-icon-base-2" />
                          {getShortId(log.session_id)}
                        </Tag>
                      </span>
                    </Tooltip>
                  ) : (
                    <None />
                  )}
                </Link>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LogDrawerHeader;
